/**
 * LogRocket session-replay integration.
 *
 * Gated on production builds with a configured app ID. Dev/demo/test/CI never
 * load the SDK — the dynamic `import()` only fires when `isLogRocketEnabled()`
 * returns true, so non-prod bundles avoid the network and runtime cost.
 *
 * Usage: call `initLogRocket()` once at startup and `identifyLogRocketUser()`
 * whenever auth state changes. Both are safe to call when disabled (no-op).
 */

import type LogRocketType from 'logrocket'
import { APP_VERSION } from '@/config/version'

type LogRocketSdk = typeof LogRocketType

interface UserTraits {
  readonly id: string
  readonly name?: string | null
  readonly email?: string | null
}

const INTERNAL_EMAIL_DOMAIN = 'virtualscienceai.com'

let sdkPromise: Promise<LogRocketSdk | null> | null = null
let initialized = false
let identifiedUserId: string | null = null

export function isInternalUserEmail(email: string | null | undefined): boolean {
  if (!email) return false
  const at = email.lastIndexOf('@')
  if (at < 0) return false
  return email.slice(at + 1).toLowerCase() === INTERNAL_EMAIL_DOMAIN
}

function readEnv(key: string): string | undefined {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return (import.meta.env as Record<string, string | undefined>)[key]
  }
  return undefined
}

function getAppId(): string | null {
  const id = readEnv('VITE_LOGROCKET_APP_ID')?.trim()
  return id ? id : null
}

export function isLogRocketEnabled(): boolean {
  if (typeof window === 'undefined') return false
  if (!import.meta.env.PROD) return false
  if (import.meta.env.VITE_DEMO_MODE === 'true') return false
  return getAppId() !== null
}

async function loadSdk(): Promise<LogRocketSdk | null> {
  if (!isLogRocketEnabled()) return null
  if (!sdkPromise) {
    sdkPromise = (async (): Promise<LogRocketSdk | null> => {
      try {
        const [{ default: LogRocket }, { default: setupLogRocketReact }] = await Promise.all([
          import('logrocket'),
          import('logrocket-react'),
        ])
        const appId = getAppId()
        if (!appId) return null
        LogRocket.init(appId, {
          release: APP_VERSION,
        })
        setupLogRocketReact(LogRocket)
        return LogRocket
      } catch (error) {
        console.warn('LogRocket failed to initialize', error)
        return null
      }
    })()
  }
  return sdkPromise
}

export function initLogRocket(): void {
  if (initialized) return
  initialized = true
  void loadSdk()
}

export function identifyLogRocketUser(traits: UserTraits | null): void {
  if (!isLogRocketEnabled()) return
  if (!traits) {
    // Reset on logout: drop the cached id so a re-login re-identifies.
    identifiedUserId = null
    return
  }
  if (identifiedUserId === traits.id) return
  identifiedUserId = traits.id
  void loadSdk().then((sdk) => {
    if (!sdk) return
    const payload: Record<string, string> = {}
    if (traits.name) payload.name = traits.name
    if (traits.email) payload.email = traits.email
    sdk.identify(traits.id, payload)
  })
}

/** @internal — testing helper to reset module-level state. */
export function __resetLogRocketForTests(): void {
  sdkPromise = null
  initialized = false
  identifiedUserId = null
}
